/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from "react";
import { HiOutlineRefresh } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import CustomPopup from "../../../Common/components/CustomPopup/CustomPopup";
import t from "../../../Common/utils/translation/translator";
import {
  selectCurrentAxisId,
  selectFiltersPopupOpened,
  selectRetrofitConfiguration,
  setAxisFilters,
  setDriveSynchronizedFilters,
  setFiltersPopupOpened,
  showSnackbar
} from "../../../Retrofit/store/slices/retrofit";
import DriveFilters from "../../components/Devices/Filters/DriveFilters";
import { logEvent, syncFiltersDependencies } from "../../../Common/utils/api/api";
import { selectConfigurationType, selectEventsData } from "../../../Common/store/slices/common";
import { useQuery } from "react-query";
import { formatConnectorType, formatMotorEncoder, formatNbPhases, formatShaftEnd } from "../../utils/func.util";

const FiltersPopup = () => {
  const filtersPopupOpened = useSelector(selectFiltersPopupOpened);
  const eventsData = useSelector(selectEventsData);
  const configurationType = useSelector(selectConfigurationType);
  const dispatch = useDispatch();

  const [initialDriveFilters, setInitialDriveFilters] = useState(null);
  const [driveFilters, setDriveFilters] = useState(null);

  const [isClearing, setIsClearing] = useState(false);
  const [shouldRerender, setShouldRerender] = useState(true);

  const axisId = useSelector(selectCurrentAxisId);
  const configuration = useSelector(selectRetrofitConfiguration);

  const axisName = configuration?.configData.axes.find((a) => a.id === axisId)?.name;

  useEffect(() => {
    // save the initial filters when opening the popup
    const axisFromConfiguration = configuration?.configData.axes.find((a) => a.id === axisId);
    setInitialDriveFilters(axisFromConfiguration?.configFilters?.driveFilter);
  }, [filtersPopupOpened]);

  useEffect(() => {
    // get the new drive filters from the configuration
    const axisFromConfiguration = configuration?.configData.axes.find((a) => a.id === axisId);
    setDriveFilters(axisFromConfiguration?.configFilters?.driveFilter);
  }, [filtersPopupOpened, configuration]);

  const {
    refetch: execSyncFilters,
    isFetching: isFetchingSyncFilters,
    isLoading: isLoadingSyncFilters
  } = useQuery(["SYNC_APPLICATION_SETTINGS_DRIVE_FILTERS", driveFilters], async () => {
    try {
      const filters = configuration.configData.axes.find((a) => a.id === axisId)?.configFilters;

      if (!driveFilters) return undefined;

      const payload = {
        locale: window.msizerConfiguration?.locale || "en-WW",
        search: {
          applicationDataFilter: {
            supplyVoltage:
              filters?.driveFilter.supplyVoltage?.length > 0 ? filters?.driveFilter.supplyVoltage : undefined,
            nominalTorqueOfMotor: filters?.motorFilter?.nominalTorque,
            nominalSpeedOfMotor: filters?.motorFilter?.nominalSpeed,
            nominalTorque: filters?.applicationDataFilter?.nominalTorque
              ? {
                  min: parseFloat(filters?.applicationDataFilter?.nominalTorque)
                  /*
              max:
                parseFloat(filters?.applicationDataFilter?.nominalTorque) *
                getNominalToqueMultiplicator(configurationType)
              */
                }
              : undefined,
            // nominalSpeed is named nominalSpeed but actually it's considered as a minimum
            nominalSpeed: filters?.applicationDataFilter?.nominalSpeed
              ? {
                  min: Number(filters?.applicationDataFilter?.nominalSpeed)
                }
              : undefined,
            withGearbox: filters?.motorFilter?.withGearbox || filters?.gearboxFilter?.enabled,
            length: filters?.applicationDataFilter?.maxLength
              ? { max: Number(filters?.applicationDataFilter?.maxLength) }
              : undefined,
            withBrake:
              filters?.motorFilter.holdingBrake?.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0]
          },
          motorFilter: {
            type: filters?.motorFilter.motorType?.length > 0 ? filters?.motorFilter.motorType : undefined,
            nominalTorqueOfMotor: filters?.motorFilter.nominalTorque,
            encoder:
              formatMotorEncoder(filters?.motorFilter.motorEncoder)?.length > 0
                ? formatMotorEncoder(filters?.motorFilter.motorEncoder)
                : undefined,
            connector:
              formatConnectorType(filters?.motorFilter.connectorType)?.length > 0
                ? formatConnectorType(filters?.motorFilter.connectorType)
                : undefined,
            flangeSize: filters?.motorFilter.flangeSize,
            withBrake:
              filters?.motorFilter.holdingBrake?.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0],
            length: filters?.motorFilter?.length,
            ipProtections:
              filters?.motorFilter.ipDegreeOfProtection?.length > 0
                ? filters?.motorFilter.ipDegreeOfProtection
                : undefined,
            shaftEnd:
              formatShaftEnd(filters?.motorFilter.shaftEnd)?.length > 0
                ? formatShaftEnd(filters?.motorFilter.shaftEnd)
                : undefined,
            peakStallTorque: filters?.motorFilter.peakStallTorque,
            continuousStallTorque: filters?.motorFilter.continuousStallTorque
          },
          driveFilter: {
            type: filters?.driveFilter.driveType?.length > 0 ? filters?.driveFilter.driveType : undefined,
            outputCurrents:
              filters?.driveFilter.outputCurrents?.length > 0 ? filters?.driveFilter.outputCurrents : undefined,
            communicationInterfaces: driveFilters?.driveInterface,
            nominalPower: filters?.driveFilter.nominalPower?.length > 0 ? filters?.driveFilter.nominalPower : undefined,
            switchingFreq:
              filters?.driveFilter.switchingFreq?.length > 0 ? filters?.driveFilter.switchingFreq : undefined,
            format: filters?.driveFilter.format?.length > 0 ? filters?.driveFilter.format : undefined,
            nbPhases: formatNbPhases(driveFilters?.nbPhases),
            outputVoltages: driveFilters?.driveVoltages
          },
          gearboxFilter: {
            manufacturer: undefined,
            type: filters?.gearboxFilter.gearboxType?.length > 0 ? filters?.gearboxFilter.gearboxType : undefined,
            precision: undefined,
            reductionRatio: filters?.gearboxFilter.ratio?.length > 0 ? filters?.gearboxFilter.ratio : undefined,
            specification: undefined,
            efficiency: filters?.gearboxFilter.efficiency?.length > 0 ? filters?.gearboxFilter.efficiency : undefined,
            productOrComponentType:
              filters?.gearboxFilter.productOrComponentType?.length > 0
                ? filters?.gearboxFilter.productOrComponentType
                : undefined,
            torsionalBacklash: filters?.gearboxFilter?.torsionalBacklash,
            inputDiameter:
              filters?.gearboxFilter.inputDiameter?.length > 0 ? filters?.gearboxFilter.inputDiameter : undefined,
            externalDiameter:
              filters?.gearboxFilter.externalDiameter?.length > 0 ? filters?.gearboxFilter.externalDiameter : undefined
          }
        }
      };

      const newFilters = await syncFiltersDependencies(configurationType, payload);

      dispatch(
        setDriveSynchronizedFilters({
          ...newFilters.drive
        })
      );

      // nb phases
      // todo later : new export DAL
      return newFilters;
    } catch (error) {
      // dispatch(showMinimalPopup(`Filters dependencies error`));
      dispatch(showSnackbar({ id: "ERROR_FILTERS" }));
      logEvent(eventsData.locale, eventsData.types.errorFilters, eventsData.tags.none, configuration?.configId);

      console.error("ERR", error);
    }
    return undefined;
  });

  const handleCancelChanges = () => {
    // reset the filters to the initial state
    const filters = {
      ...configuration.configData.axes.find((a) => a.id === axisId).configFilters,
      driveFilter: initialDriveFilters
    };
    setDriveFilters(initialDriveFilters);
    dispatch(setAxisFilters({ axisId, filters }));
  };

  const handleClearFilters = () => {
    setIsClearing(true);
    setTimeout(() => {
      setIsClearing(false);
    }, 500);
    const filters = {
      ...configuration.configData.axes.find((a) => a.id === axisId).configFilters,
      driveFilter: initialDriveFilters
    };
    setDriveFilters(initialDriveFilters);
    dispatch(setAxisFilters({ axisId, filters }));
    setShouldRerender(!shouldRerender);
  };

  return (
    <CustomPopup
      headerTitle={`${t("msizer.ui.popup.edit_application_settings.title")} - ${axisName}`}
      opened={filtersPopupOpened}
      onClose={() => {
        handleCancelChanges();
        dispatch(setFiltersPopupOpened(false));
      }}
      cancelAction={() => {
        handleCancelChanges();
        dispatch(setFiltersPopupOpened(false));
      }}
      continueLabel={t("msizer.ui.popup.button.apply")}
      cancelLabel={t("msizer.ui.popup.button.cancel")}
      continueAction={() => {
        dispatch(
          setAxisFilters({
            axisId,
            filters: {
              ...configuration?.configData.axes.find((a) => a.id === axisId)?.configFilters,
              driveFilter: driveFilters
            }
          })
        );
        dispatch(setFiltersPopupOpened(false));
      }}
      // disabled if nbPhases, driveVoltages or driveInterface are empty
      continueDisabled={
        driveFilters?.nbPhases?.length === 0 ||
        driveFilters?.driveVoltages?.length === 0 ||
        driveFilters?.driveInterface?.length === 0
      }
      closeOnDocumentClick={false}
    >
      <div>
        <div className="p-2 pt-0 space-y-5 text-sm">
          <div
            className="flex-1 flex items-center justify-end text-sm text-secondary
"
          >
            <div
              onClick={() => {
                handleClearFilters();
              }}
              className="flex items-center space-x-1 cursor-pointer"
            >
              <HiOutlineRefresh className={isClearing && "animate-spin"} />
              <span>{t("msizer.ui.filters.reset_filters")}</span>
            </div>
          </div>
          <DriveFilters
            filters={configuration?.configData.axes.find((a) => a.id === axisId)?.configFilters}
            filterToDisplay="applicationSettings"
            shouldRerender={shouldRerender}
            updateDriveFilters={(setFilters) => {
              setDriveFilters(setFilters.drive);
            }}
            isLoading={isLoadingSyncFilters || isFetchingSyncFilters}
            data-testid="drive-filters"
          />
        </div>
      </div>
    </CustomPopup>
  );
};

export default FiltersPopup;
